// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { DataTableBody, DataTableHead, DataTableHeader, Modal } from "@insite/mobius/index";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import { tableGetEllipsisCellProps, tableProps } from "@store/Themes/common/props/table";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import { css } from "styled-components";

export interface WishListNonValidItemModel {
    productId: string;
    shortDescription: string;
    erpNumber: string;
    reason: string;
}

export interface WishListInvalidProductsWarningModalStyles {
    modal?: ModalPresentationProps;
    descriptionText?: TypographyProps;
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    tableHead?: DataTableHeadProps;
    rowNumberHeader?: DataTableHeaderProps;
    itemNumberHeader?: DataTableHeaderProps;
    descriptionHeader?: DataTableHeaderProps;
    reasonHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    rowNumberCell?: DataTableCellProps;
    productNumberCell?: DataTableCellProps;
    descriptionCell?: DataTableCellProps;
    reasonCell?: DataTableCellProps;
    continueButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
}

export const wishListInvalidProductsWarningModalStyles: WishListInvalidProductsWarningModalStyles = {
    modal: { sizeVariant: "medium" },
    wrapper: tableProps.wrapper,
    dataTable: {
        cssOverrides: {
            table: css`
                ${(x: any) => x.theme.dataTable?.defaultProps?.cssOverrides?.table}
                margin-top: ${pxToRem(15)};
            `,
        },
    },
    descriptionCell: tableGetEllipsisCellProps(346),
    reasonCell: tableGetEllipsisCellProps(346),
    cancelButton: { variant: "secondary" },
    continueButton: { variant: "primary" },
};

interface Props {
    wishListName: string;
    isOpen: boolean;
    nonValidItems: WishListNonValidItemModel[];
    onClose: () => void;
    onAfterClose: (e: any) => void;
    onContinue: () => void;
    extendedStyles?: WishListInvalidProductsWarningModalStyles;
}

const WishListInvalidProductsWarningModal = ({
    wishListName,
    isOpen,
    nonValidItems,
    onClose,
    onAfterClose,
    onContinue,
    extendedStyles,
}: Props) => {
    const styles = mergeToNew(wishListInvalidProductsWarningModalStyles, extendedStyles);
    const modalHeadline = translate("Add List to Cart: {0}", wishListName);

    return (
        <Modal
            {...styles.modal}
            headline={modalHeadline}
            isOpen={isOpen}
            handleClose={onClose}
            onAfterClose={onAfterClose}
            data-test-selector="wishList_addListToCartWarning"
        >
            <Typography {...styles.descriptionText}>
                {translate("Some items in your list are no longer valid for purchase and won't be added to your cart.")}
            </Typography>
            <StyledWrapper {...styles.wrapper}>
                <DataTable {...styles.dataTable}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.rowNumberHeader}>{translate("Row")}</DataTableHeader>
                        <DataTableHeader {...styles.itemNumberHeader}>{translate("Item #")}</DataTableHeader>
                        <DataTableHeader {...styles.descriptionHeader}>{translate("Description")}</DataTableHeader>
                        <DataTableHeader {...styles.reasonHeader}>{translate("Reason")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {nonValidItems.map(({ shortDescription, erpNumber, reason }, index) => {
                            const rowNumber = index + 1;

                            return (
                                <DataTableRow key={rowNumber} {...styles.tableRow}>
                                    <DataTableCell
                                        {...styles.rowNumberCell}
                                        data-test-selector={`row_${rowNumber}_RowNumber`}
                                    >
                                        {rowNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.productNumberCell}
                                        data-test-selector={`row_${rowNumber}_ProductNumber`}
                                    >
                                        {erpNumber}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.descriptionCell}
                                        data-test-selector={`row_${rowNumber}_Description`}
                                    >
                                        {shortDescription}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.reasonCell}
                                        data-test-selector={`row_${rowNumber}_Reason`}
                                    >
                                        <Typography>{reason}</Typography>
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            <FormActions>
                <Button {...styles.continueButton} onClick={onContinue} data-test-selector="wishListModal_Continue">
                    {translate("Continue Add to cart")}
                </Button>
                <Button {...styles.cancelButton} onClick={onClose} data-test-selector="wishListModal_Cancel">
                    {translate("Cancel")}
                </Button>
            </FormActions>
        </Modal>
    );
};

export default WishListInvalidProductsWarningModal;
